import * as React from 'react'
import { styled, css } from 'next-yak'

import { t } from '@owl-nest/localize'
import * as model from '@owl-nest/models'
import { CATEGORY, EVENT_TYPE, useTracking } from '@owl-nest/shadow'
import * as env from '@owl-nest/config'

import * as plume from '@ulule/owl-kit-components/next'

import * as projectModel from '../../../../models/project'
import { TRACKING_ACTION, TRACKING_SOURCE } from '../../utils/trackerEnums'

type ProjectFinishedDonationBasedProps = {
  project: projectModel.Project
  fullSize: boolean
}

export function ProjectFinishedDonationBased({
  project,
  fullSize,
}: ProjectFinishedDonationBasedProps): React.ReactElement<ProjectFinishedDonationBasedProps> {
  const offboardingType: string | undefined = project.onboarding_steps?.offboarding?.type

  const tracking = useTracking()
  const userConfig = env.useUserEnv()

  const postCampaignUrl = project.post_campaign_link?.url
  const hasOnboarding = postCampaignUrl !== undefined

  let button = t('Order')
  if (offboardingType === 'support') {
    button = t('Back the project')
  } else if (offboardingType === 'follow') {
    button = t('Follow the project')
  }

  return (
    <ProjectFinishedDonationBasedContainer $fullSize={fullSize}>
      <InformationContainer $fullSize={fullSize}>
        <plume.styles.heading.S asDesktop="L">
          <plume.illustrations.twoToned.CrackedEgg size={60} noBackground inverted />
          {t(`%(name)s's project is finished!`, { name: model.user.username(project.owner) })}
        </plume.styles.heading.S>

        {hasOnboarding ? (
          <>
            <plume.styles.copy.M.light>{t('Good news, we have continued selling here 👇')}</plume.styles.copy.M.light>
            <plume.styles.copy.M.light>
              <plume.styles.button.Button size="medium" kind="primary" as="a" href={postCampaignUrl} onClick={track}>
                {button}
              </plume.styles.button.Button>
            </plume.styles.copy.M.light>
          </>
        ) : (
          <>
            <plume.styles.copy.M.light>
              {t('Discover more %(mainTag)s live projects 👇', {
                mainTag: model.project.mainTag<projectModel.ExtraFields>(project),
              })}
            </plume.styles.copy.M.light>

            <plume.styles.button.Button size="medium" kind="primary" as="a" href={project.main_tag?.absolute_url}>
              {t(`See all %(category)s projects`, {
                category: project.main_tag?.name[userConfig.lang],
              })}
            </plume.styles.button.Button>
          </>
        )}
      </InformationContainer>
    </ProjectFinishedDonationBasedContainer>
  )

  function track() {
    if (offboardingType !== undefined) {
      tracking.track({
        event: EVENT_TYPE.GENERIC,
        eventCategory: CATEGORY.PROJECT_PAGE,
        eventAction: TRACKING_ACTION.CLICK_OFFBOARDING,
        eventLabel: TRACKING_SOURCE.BACKER_BLOCK,
        eventValue: offboardingType,
      })
    }
  }
}

const ProjectFinishedDonationBasedContainer = styled.div<{ $fullSize: boolean }>`
  display: block;
  text-align: center;
  padding: 20px 0px;
  width: 100%;

  // NOT SCREEN !
  @media not screen and ${plume.BREAKPOINTS.LAPTOP} {
    ${plume.illustrations.Illustration} {
      margin-bottom: 5px;
    }
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    display: flex;
    padding: 0;
    max-width: 790px;

    ${({ $fullSize }) => {
      if ($fullSize) {
        return css`
          justify-content: center;
        `
      } else {
        return css`
          text-align: left;
        `
      }
    }}
  }
`

const InformationContainer = styled.div<{ $fullSize: boolean }>`
  box-sizing: border-box;
  padding: 0 20px;
  width: 100%;

  ${({ $fullSize }) => {
    if ($fullSize) {
      return css`
        align-items: center;
        display: flex;
        flex-direction: column;
      `
    }
  }}

  ${plume.styles.heading.S} {
    align-items: center;
    display: flex;
    margin-bottom: 6px;
  }

  ${plume.styles.button.Button} {
    width: 100%;
  }

  ${plume.Link} {
    margin-top: 20px;
  }

  ${plume.styles.copy.M.light} {
    margin-bottom: 16px;
  }

  // NOT SCREEN !
  @media not screen and ${plume.BREAKPOINTS.LAPTOP} {
    ${plume.styles.heading.S} {
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      margin-bottom: 0px;
    }
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    ${plume.styles.heading.S} {
      ${plume.illustrations.Illustration} {
        margin-right: 8px;
      }
    }

    ${plume.styles.button.Button} {
      width: auto;
    }
  }
`
